import { useCallback, useEffect } from 'react'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'

import AnalyticsPostCampaignReportPost from '@components/Analytics/PostCampaignReport/Post/Post'
import { AnalyticsPostCampaignReportReel } from '@components/Analytics/PostCampaignReport/Reel/Reel'
import AnalyticsPostCampaignReportStory from '@components/Analytics/PostCampaignReport/Story/Story'
import AnalyticsPostCampaignReportSummary from '@components/Analytics/PostCampaignReport/Summary/Summary'
import AnalyticsPostCampaignReportTikTok from '@components/Analytics/PostCampaignReport/TikTok/TikTok'
import { PostCampaignReportChannelSummary } from '@components/Reports/PostCampaignReport/Channel/Summary/Summary'
import Loading from '@components/UI/Loading'
import { PCRChannels } from '@enums'
import { IApplicationState } from '@store'
import { selectIsCampaignFetched } from '@store/campaigns'
import {
  fetchCampaignMetrics,
  fetchPostMetrics,
  fetchReelMetrics,
  fetchSnapMetrics,
  fetchStoryMetrics,
  fetchTikTokMetrics,
  selectIsCampaignMetricsInErrorState,
  selectIsSummaryActive,
  selectIsSummaryFetched,
  selectShouldFetchPostMetrics,
  selectShouldFetchReelMetrics,
  selectShouldFetchStoryMetrics,
  selectShouldFetchTikTokMetrics,
  selectShowLoading,
} from '@store/postCampaignReport'
import { fetchPcrMetrics } from '@store/postCampaignReport/metrics/action'
import { fetchXMetrics } from '@store/postCampaignReport/xMetrics/actions'
import { selectActivePCRChannel, selectCampaignId } from '@store/router'
import PostCampaignReportPerformanceList from '../List'

const PCR_CHANNEL_SUMMARY_SUPPORTED_CHANNELS = [
  PCRChannels.X,
  PCRChannels.Snap,
  PCRChannels.YouTubeShorts,
  PCRChannels.YouTubeVideos,
]

const PostCampaignReportChannelSummaryWrapper: React.FC<{
  activeChannel: PCRChannels
  campaignId: number
}> = ({ activeChannel, campaignId }) => {
  const dispatch = useDispatch()
  const { isCampaignFetched, isSummaryFetched } = useSelector((state: IApplicationState) => {
    return {
      isCampaignFetched: selectIsCampaignFetched(state, campaignId),
      isSummaryFetched: selectIsSummaryFetched(state, campaignId),
    }
  }, shallowEqual)

  useEffect(() => {
    if (isCampaignFetched && isSummaryFetched) {
      if (activeChannel === PCRChannels.X) {
        dispatch(fetchXMetrics(campaignId))
      } else if (activeChannel === PCRChannels.Snap) {
        dispatch(fetchSnapMetrics(campaignId))
      } else if (
        activeChannel === PCRChannels.YouTubeShorts ||
        activeChannel === PCRChannels.YouTubeVideos
      ) {
        dispatch(fetchPcrMetrics(campaignId, activeChannel))
      }
    }
  }, [activeChannel, isCampaignFetched, isSummaryFetched])

  return (
    <PostCampaignReportChannelSummary
      channel={activeChannel}
      translationPrefix={`postCampaignReport.summary.${activeChannel}`}
    />
  )
}

export interface IInternalProps {
  activeChannel: PCRChannels
  campaignId: number
  isCampaignFetched?: boolean
  isSummaryFetched?: boolean
  showLoading?: boolean
  isCampaignMetricsInErrorState?: boolean
  shouldFetchPostMetrics?: boolean
  shouldFetchVideoMetrics?: boolean
  shouldFetchStoryMetrics?: boolean
  shouldFetchTikTokMetrics?: boolean
  shouldFetchReelMetrics?: boolean
  isSummaryActive?: boolean
  fetchCampaignMetrics: typeof fetchCampaignMetrics
  fetchPostMetrics: typeof fetchPostMetrics
  fetchStoryMetrics: typeof fetchStoryMetrics
}

const PostCampaignReportPerformanceListWrapper: React.FC<IInternalProps> = ({
  activeChannel,
  campaignId,
  isCampaignFetched,
  showLoading,
  isCampaignMetricsInErrorState,
  shouldFetchPostMetrics,
  shouldFetchStoryMetrics,
  shouldFetchTikTokMetrics,
  shouldFetchReelMetrics,
  isSummaryActive,
  fetchCampaignMetrics: fetchCampaignMetricsAction,
  fetchPostMetrics: fetchPostMetricsAction,
  fetchStoryMetrics: fetchStoryMetricsAction,
}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    if (isCampaignFetched && !isCampaignMetricsInErrorState) {
      fetchCampaignMetricsAction(campaignId)
    }

    if (shouldFetchPostMetrics) {
      fetchPostMetricsAction(campaignId, activeChannel)
    }

    if (shouldFetchStoryMetrics) {
      fetchStoryMetricsAction(campaignId, activeChannel)
    }

    if (shouldFetchTikTokMetrics) {
      dispatch(fetchTikTokMetrics(campaignId, activeChannel))
    }

    if (shouldFetchReelMetrics) {
      dispatch(fetchReelMetrics(campaignId))
    }
  }, [
    isCampaignFetched,
    activeChannel,
    campaignId,
    isCampaignMetricsInErrorState,
    shouldFetchPostMetrics,
    shouldFetchStoryMetrics,
    shouldFetchTikTokMetrics,
    shouldFetchReelMetrics,
  ])

  if (showLoading) {
    return <Loading height={30} />
  }

  if (isSummaryActive) {
    return <AnalyticsPostCampaignReportSummary />
  }

  if (activeChannel === PCRChannels.IGPosts) {
    return <AnalyticsPostCampaignReportPost />
  }

  if (activeChannel === PCRChannels.IGStories) {
    return <AnalyticsPostCampaignReportStory />
  }

  if (activeChannel === PCRChannels.TikTok) {
    return <AnalyticsPostCampaignReportTikTok />
  }

  if (activeChannel === PCRChannels.Reel) {
    return <AnalyticsPostCampaignReportReel />
  }

  if (PCR_CHANNEL_SUMMARY_SUPPORTED_CHANNELS.includes(activeChannel)) {
    return (
      <PostCampaignReportChannelSummaryWrapper
        activeChannel={activeChannel}
        campaignId={campaignId}
      />
    )
  }

  return <PostCampaignReportPerformanceList />
}

const PostCampaignReportPerformanceListWrapperConnected: React.FC = () => {
  const match = useRouteMatch()
  const dispatch = useDispatch()
  const activeChannel = useSelector((state: IApplicationState) => selectActivePCRChannel(state))
  const campaignId = selectCampaignId(match)!
  const isCampaignFetched = useSelector((state: IApplicationState) =>
    selectIsCampaignFetched(state, campaignId),
  )
  const showLoading = useSelector((state: IApplicationState) => selectShowLoading(state))
  const shouldFetchPostMetrics = useSelector((state: IApplicationState) =>
    selectShouldFetchPostMetrics(state, campaignId, activeChannel),
  )
  const shouldFetchStoryMetrics = useSelector((state: IApplicationState) =>
    selectShouldFetchStoryMetrics(state, campaignId, activeChannel),
  )
  const shouldFetchTikTokMetrics = useSelector((state: IApplicationState) =>
    selectShouldFetchTikTokMetrics(state, campaignId, activeChannel),
  )
  const shouldFetchReelMetrics = useSelector((state: IApplicationState) =>
    selectShouldFetchReelMetrics(state, campaignId, activeChannel),
  )
  const isCampaignMetricsInErrorState = useSelector((state: IApplicationState) =>
    selectIsCampaignMetricsInErrorState(state),
  )
  const isSummaryActive = useSelector((state: IApplicationState) => selectIsSummaryActive(state))
  const fetchCampaignMetricsCallback = useCallback(
    (cid) => dispatch(fetchCampaignMetrics(cid)),
    [dispatch],
  )
  const fetchPostMetricsCallback = useCallback(
    (cid, channel) => dispatch(fetchPostMetrics(cid, channel)),
    [dispatch],
  )
  const fetchStoryMetricsCallback = useCallback(
    (cid, channel) => dispatch(fetchStoryMetrics(cid, channel)),
    [dispatch],
  )
  return (
    <PostCampaignReportPerformanceListWrapper
      activeChannel={activeChannel}
      campaignId={campaignId}
      isCampaignFetched={isCampaignFetched}
      showLoading={showLoading}
      shouldFetchPostMetrics={shouldFetchPostMetrics}
      shouldFetchStoryMetrics={shouldFetchStoryMetrics}
      shouldFetchTikTokMetrics={shouldFetchTikTokMetrics}
      shouldFetchReelMetrics={shouldFetchReelMetrics}
      isCampaignMetricsInErrorState={isCampaignMetricsInErrorState}
      isSummaryActive={isSummaryActive}
      fetchCampaignMetrics={fetchCampaignMetricsCallback}
      fetchPostMetrics={fetchPostMetricsCallback}
      fetchStoryMetrics={fetchStoryMetricsCallback}
    />
  )
}

export { PostCampaignReportPerformanceListWrapper }
export default PostCampaignReportPerformanceListWrapperConnected
